import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { PixelFrame } from '@models/frame.model';
import { SpriteLayer } from '@models/layer.model';
import type { FrameSnapshot } from '@store/reducers/frame.reducer';

export const FrameActions = createActionGroup({
  source: 'Frames',
  events: {
    // ── Frame CRUD ──────────────────────────────────────────────
    'Add Blank Frame':   emptyProps(),
    'Duplicate Frame':   props<{ frameId: string }>(),
    'Delete Frame':      props<{ frameId: string }>(),
    'Select Frame':      props<{ frameId: string }>(),
    'Clear Frame':       props<{ frameId: string }>(),
    /** Clear the pixel data for a single cel (one layer on one frame). */
    'Clear Cel':         props<{ frameId: string; layerId: string }>(),
    'Move Frame':        props<{ fromIndex: number; toIndex: number }>(),
    /** Full project replace – strips all layers/cels too. Pass both when loading. */
    'Set Frames':        props<{
      frames: PixelFrame[];
      layers?: SpriteLayer[];
      cels?: Record<string, string[][]>;
      activeLayerId?: string | null;
    }>(),
    'Restore Frame State': props<{
      frames: PixelFrame[];
      layers: SpriteLayer[];
      cels: Record<string, string[][]>;
      activeLayerId: string | null;
      currentFrameId: string | null;
      past?: FrameSnapshot[];
      future?: FrameSnapshot[];
    }>(),
    /** Append new frames; cels supplies per-cell pixel data keyed by frameId|layerId. */
    'Append Frames':     props<{ frames: PixelFrame[]; cels?: Record<string, string[][]> }>(),
    'Tween Frames':      props<{ fromFrameId: string; toFrameId: string; steps: number }>(),

    // ── Pixel painting (operates on activeLayerId, auto-creates cel) ─
    'Paint Pixel':       props<{ frameId: string; row: number; col: number; color: string }>(),
    'Paint Pixels':      props<{ frameId: string; cells: { row: number; col: number; color: string }[] }>(),
    'Fill Pixels':       props<{ frameId: string; row: number; col: number; color: string }>(),
    'Fill Pixels Symmetry': props<{ frameId: string; points: { row: number; col: number }[]; color: string }>(),
    /** Replace the active layer's cel on the given frame. */
    'Replace Frame Pixels': props<{ frameId: string; pixels: string[][] }>(),
    'Replace Frame Pixels Live': props<{ frameId: string; pixels: string[][] }>(),
    /** Collab: overwrite composite pixels directly – no layer compositing. */
    'Sync Remote Frame': props<{ frameId: string; pixels: string[][] }>(),

    // ── Selection operations ─────────────────────────────────────
    'Apply Selection Paste': props<{ frameId: string; pixels: string[][]; destRow: number; destCol: number }>(),
    'Apply Selection Fill':  props<{ frameId: string; r1: number; c1: number; r2: number; c2: number; color: string }>(),
    'Cut Selection':         props<{ frameId: string; r1: number; c1: number; r2: number; c2: number }>(),
    'Apply Selection Transform': props<{
      frameId: string;
      origin: { r1: number; c1: number; r2: number; c2: number };
      pixels: string[][];
      destRow: number;
      destCol: number;
    }>(),

    // ── Layer operations (global – no frameId needed) ────────────
    'Add Layer':             emptyProps(),
    'Delete Layer':          props<{ layerId: string }>(),
    'Select Layer':          props<{ layerId: string }>(),
    'Rename Layer':          props<{ layerId: string; name: string }>(),
    'Toggle Layer Visible':  props<{ layerId: string }>(),
    'Toggle Layer Locked':   props<{ layerId: string }>(),
    'Toggle Layer Continuous': props<{ layerId: string }>(),
    'Set Layer Opacity':     props<{ layerId: string; opacity: number }>(),
    'Set Layer Blend':       props<{ layerId: string; blendMode: string }>(),
    'Set Layer Tint':        props<{ layerId: string; tintColor: string | null }>(),
    'Set Layer Hold':        props<{ layerId: string; holdDuration: number }>(),
    'Move Layer Up':         props<{ layerId: string }>(),
    'Move Layer Down':       props<{ layerId: string }>(),
    /** Merge active layer down into the layer below, on the current frame only. */
    'Merge Layers Down':     props<{ layerId: string }>(),
    /** Resize cels on all frames to new canvas dimensions. */
    'Resize Canvas':         props<{ width: number; height: number }>(),

    // ── Undo/Redo ────────────────────────────────────────────────
    'Save Frames':   emptyProps(),
    'Begin Stroke':  emptyProps(),
    'Commit Stroke': emptyProps(),
    'Undo':          emptyProps(),
    'Redo':          emptyProps(),
    /**
     * Collab-safe undo: only reverts cels belonging to the layer that was
     * active when the snapshot was taken. Other users' layers are untouched.
     */
    'Undo Collab':   emptyProps(),
    /** Collab-safe redo — mirrors UndoCollab. */
    'Redo Collab':   emptyProps(),

    // ── Collab-only frame sync ────────────────────────────────────
    /** Reorder/add frames without clearing undo or persisting. */
    'Sync Remote Frame Order': props<{ frames: PixelFrame[] }>(),
    /** Delete a remote frame without triggering re-broadcast. */
    'Sync Remote Frame Delete': props<{ frameId: string }>(),
    /** Replace all frames on initial join – NOT in collabFrameStructure$ to avoid feedback loop. */
    'Sync Remote Full Sync': props<{
      frames: PixelFrame[];
      layers?: SpriteLayer[];
      cels?: Record<string, string[][]>;
      activeLayerId?: string | null;
    }>(),
    /** Receive layer structure from a remote peer. */
    'Sync Remote Layers': props<{ layers: SpriteLayer[]; activeLayerId: string | null }>(),
    /** Receive cel pixel data from a remote peer. */
    'Sync Remote Cels': props<{ cels: Record<string, string[][]> }>(),
    /**
     * A remote peer cleared one or more cels (e.g. via collab undo reverting to an
     * empty layer). Remove those keys from the local cel map and recomposite.
     */
    'Sync Remote Cel Clear': props<{ celKeys: string[] }>(),

    // ── Persistence ──────────────────────────────────────────────
    'Load Frames':         emptyProps(),
    'Load Frames Success': props<{
      frames: PixelFrame[];
      layers: SpriteLayer[];
      cels: Record<string, string[][]>;
      activeLayerId: string | null;
      currentFrameId: string | null;
      past?: FrameSnapshot[];
      future?: FrameSnapshot[];
    }>(),
    'Load Frames Failure': props<{ error: any }>(),
  }
});
